/**
 * AgentTaskEditor — full-page task editor (replaces the modal card in AgentTasksManager).
 *
 * Route: /manage/cloudgm-business/agent-tasks/new         → create
 *        /manage/cloudgm-business/agent-tasks/edit/:id    → edit
 *
 * A task has two possible interaction modes:
 *   • "ai"   — clicking the task on the merchant platform opens a chat with
 *              the selected AI agent (company_lead_gen, trade_license_renew…).
 *   • "form" — clicking the task on the merchant platform opens a dynamic
 *              form built here by the supervisor. Field types supported:
 *                short_text | long_text | number | email | file
 *
 * The task is also flagged with `status` (active | draft). Only "active"
 * tasks show up on the merchant platform. The card in the list view exposes
 * a toggle to flip status without opening the editor.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Loader2, Plus, Trash2, Sparkles, GripVertical,
  Bot, ListTodo, Type, AlignLeft, Hash, Mail, Paperclip, Percent, X,
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/authHeaders';
import AiAgentPicker from '../manage-platform/tabs/AiAgentPicker';
import DashboardHeader from '../../components/DashboardHeader';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { id: 'short_text', ar: 'نص قصير',   en: 'Short text',   icon: Type },
  { id: 'long_text',  ar: 'نص طويل',   en: 'Long text',    icon: AlignLeft },
  { id: 'number',     ar: 'رقم',       en: 'Number',       icon: Hash },
  { id: 'email',      ar: 'بريد',      en: 'Email',        icon: Mail },
  { id: 'file',       ar: 'مرفق',     en: 'Attachment',   icon: Paperclip },
];

const FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'docx', 'xlsx', 'txt', 'zip'];

const EMPTY = {
  title_ar: '',
  title_en: '',
  description_ar: '',
  description_en: '',
  profit_tiers: [{ percentage: '', description_ar: '', description_en: '' }],
  status: 'active',
  mode: 'ai',
  form_schema: [],
  uses_ai_agent: true,
  ai_agent_id: null,
};

const newFieldId = () => `f_${Math.random().toString(36).slice(2, 10)}`;

export default function AgentTaskEditor() {
  const navigate = useNavigate();
  const { id: taskId } = useParams(); // present in edit mode
  const isEdit = Boolean(taskId);
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const load = useCallback(async () => {
    if (!isEdit) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/agent-tasks`, { headers: getAuthHeaders() });
      const task = (res.data?.data || []).find((x) => x.id === taskId);
      if (!task) {
        toast.error(t('المهمة غير موجودة', 'Task not found'));
        navigate(-1);
        return;
      }
      const resolvedMode = task.mode || (task.uses_ai_agent ? 'ai' : 'form');
      setForm({
        ...EMPTY,
        ...task,
        profit_tiers: Array.isArray(task.profit_tiers) && task.profit_tiers.length > 0
          ? task.profit_tiers
          : EMPTY.profit_tiers,
        form_schema: Array.isArray(task.form_schema) ? task.form_schema : [],
        mode: resolvedMode,
        uses_ai_agent: resolvedMode === 'ai',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل تحميل المهمة', 'Failed to load task'));
    } finally {
      setLoading(false);
    }
  }, [taskId, isEdit]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // — Profit tiers
  const addTier = () => setF('profit_tiers', [...form.profit_tiers, { percentage: '', description_ar: '', description_en: '' }]);
  const removeTier = (i) => setF('profit_tiers', form.profit_tiers.filter((_, idx) => idx !== i));
  const setTier = (i, k, v) => setF('profit_tiers', form.profit_tiers.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  // — Form schema
  const addField = (typeId) => {
    const def = FIELD_TYPES.find((x) => x.id === typeId);
    setF('form_schema', [
      ...form.form_schema,
      {
        id: newFieldId(),
        field_type: typeId,
        label_ar: '',
        label_en: '',
        placeholder_ar: '',
        placeholder_en: '',
        required: true,
        order: form.form_schema.length,
        allowed_types: typeId === 'file' ? ['pdf', 'jpg', 'png'] : [],
        max_size_mb: 10,
      },
    ]);
    toast.success(t(`تمت إضافة ${def.ar}`, `Added ${def.en}`));
  };
  const removeField = (idx) => setF('form_schema', form.form_schema.filter((_, i) => i !== idx));
  const updField = (idx, patch) =>
    setF('form_schema', form.form_schema.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  // — Save
  const submit = async () => {
    if (!form.title_ar.trim()) {
      toast.error(t('عنوان المهمة بالعربية مطلوب', 'Arabic title is required'));
      return;
    }
    if (form.mode === 'ai' && !form.ai_agent_id) {
      toast.error(t('اختر وكيل AI للمهمة', 'Select an AI agent for the task'));
      return;
    }
    if (form.mode === 'form' && form.form_schema.length === 0) {
      toast.error(t('أضف حقلاً واحداً على الأقل للنموذج', 'Add at least one form field'));
      return;
    }
    // Clean tiers
    const cleanTiers = (form.profit_tiers || [])
      .map((tr) => ({
        percentage: parseFloat(tr.percentage) || 0,
        description_ar: (tr.description_ar || '').trim(),
        description_en: (tr.description_en || '').trim(),
      }))
      .filter((tr) => tr.percentage > 0);

    const payload = {
      title_ar: form.title_ar.trim(),
      title_en: (form.title_en || '').trim(),
      description_ar: (form.description_ar || '').trim(),
      description_en: (form.description_en || '').trim(),
      profit_tiers: cleanTiers,
      status: form.status,
      mode: form.mode,
      form_schema: form.form_schema,
      uses_ai_agent: form.mode === 'ai',
      ai_agent_id: form.mode === 'ai' ? form.ai_agent_id : null,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await axios.patch(`${API_URL}/api/admin/agent-tasks/${taskId}`, payload, { headers: getAuthHeaders() });
        toast.success(t('تم حفظ التعديلات', 'Changes saved'));
      } else {
        await axios.post(`${API_URL}/api/admin/agent-tasks`, payload, { headers: getAuthHeaders() });
        toast.success(t('تم إنشاء المهمة', 'Task created'));
      }
      navigate(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
        <DashboardHeader onBack={() => navigate(-1)} />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'} data-testid="agent-task-editor-page">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Unified Header (same as every other page) */}
      <DashboardHeader onBack={() => navigate(-1)} />

      {/* Breadcrumb — matches the app-wide pattern */}
      <div className="border-b border-white/5 relative z-10" data-testid="agent-task-editor-breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 text-sm flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
            >
              {t('لوحة التحكم', 'Dashboard')}
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <button
              onClick={() => navigate('/dashboard?view=cloudgm')}
              className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
            >
              CLOUDGM FZ LLC
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <button
              onClick={() => navigate('/manage/cloudgm-business')}
              className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
            >
              {t('منصة التجار', 'Merchant Platform')}
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <button
              onClick={() => navigate('/manage/cloudgm-business?tab=add-agent-task')}
              className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer"
            >
              {t('مهام الوكلاء', 'Agent Tasks')}
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <span className="text-[#d4af37]">
              {isEdit ? t('تعديل مهمة', 'Edit Task') : t('إدراج مهمة جديدة', 'Add New Task')}
            </span>
          </div>
        </div>
      </div>

      <main className="relative z-10 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page title + primary action */}
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-2xl bg-cyan-500/10 border border-cyan-500/20">
                <ListTodo className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">
                  {isEdit ? t('تعديل مهمة الوكيل', 'Edit Agent Task') : t('إدراج مهمة جديدة', 'Add New Task')}
                </h1>
                <p className="text-white/50 text-sm mt-1">
                  {t(
                    'كل مهمة تصل مباشرة إلى الوكلاء المُفعّلين على منصة التجار.',
                    'Every task is pushed live to enabled agents on the merchant platform.',
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#d4af37] text-black font-bold hover:bg-[#d4af37]/90 disabled:opacity-40 transition"
              data-testid="agent-task-editor-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isEdit ? t('حفظ التعديلات', 'Save Changes') : t('إنشاء المهمة', 'Create Task')}
            </button>
          </div>

        {/* Section 1: Basic info */}
        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            {t('المعلومات الأساسية', 'Basic Info')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t('عنوان المهمة (عربي) *', 'Title AR *')}>
              <input
                type="text"
                value={form.title_ar}
                onChange={(e) => setF('title_ar', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 text-white focus:border-cyan-500 outline-none"
                placeholder={t('مثال: إحضار شركات جديدة', 'e.g., Onboard new companies')}
                data-testid="task-editor-title-ar"
              />
            </Field>
            <Field label={t('عنوان المهمة (إنجليزي)', 'Title EN')}>
              <input
                type="text"
                value={form.title_en}
                onChange={(e) => setF('title_en', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 text-white focus:border-cyan-500 outline-none"
                dir="ltr"
                data-testid="task-editor-title-en"
              />
            </Field>
            <Field label={t('الوصف (عربي)', 'Description AR')} className="sm:col-span-2">
              <textarea
                rows={3}
                value={form.description_ar}
                onChange={(e) => setF('description_ar', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 text-white focus:border-cyan-500 outline-none resize-none"
                data-testid="task-editor-desc-ar"
              />
            </Field>
            <Field label={t('الوصف (إنجليزي)', 'Description EN')} className="sm:col-span-2">
              <textarea
                rows={3}
                value={form.description_en}
                onChange={(e) => setF('description_en', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 text-white focus:border-cyan-500 outline-none resize-none"
                dir="ltr"
                data-testid="task-editor-desc-en"
              />
            </Field>
          </div>
        </section>

        {/* Section 2: Profit tiers */}
        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Percent className="w-5 h-5 text-emerald-400" />
              {t('طبقات الأرباح', 'Profit Tiers')}
            </h2>
            <button
              onClick={addTier}
              className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
              data-testid="task-editor-add-tier"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('طبقة جديدة', 'Add tier')}
            </button>
          </div>
          <div className="space-y-3">
            {form.profit_tiers.map((tier, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-start p-3 bg-[#0a0a0a] rounded-xl border border-white/5">
                <div className="col-span-2">
                  <div className="relative">
                    <input
                      type="number"
                      value={tier.percentage}
                      onChange={(e) => setTier(i, 'percentage', e.target.value)}
                      className="w-full px-2 py-2 rounded-lg bg-black/40 border border-white/10 text-emerald-400 font-bold text-center focus:border-emerald-500 outline-none"
                      placeholder="0"
                      data-testid={`task-editor-tier-percentage-${i}`}
                    />
                    <span className="absolute right-2 top-2.5 text-emerald-400 text-xs">%</span>
                  </div>
                </div>
                <div className="col-span-9">
                  <input
                    type="text"
                    value={tier.description_ar}
                    onChange={(e) => setTier(i, 'description_ar', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white focus:border-emerald-500 outline-none text-sm"
                    placeholder={t('وصف الطبقة (عربي) — مثال: على كل شركة جديدة', 'Tier description (AR)')}
                    data-testid={`task-editor-tier-desc-${i}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  {form.profit_tiers.length > 1 && (
                    <button
                      onClick={() => removeTier(i)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                      data-testid={`task-editor-tier-remove-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: Mode selector */}
        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4">
            {t('نمط تفاعل الوكيل مع المهمة', 'Interaction Mode')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <ModeCard
              active={form.mode === 'ai'}
              onClick={() => setF('mode', 'ai')}
              icon={Bot}
              title={t('محادثة مع وكيل AI', 'AI Chat Agent')}
              desc={t(
                'يفتح الوكيل محادثة مع AI يجمع معه البيانات ويولّد التقديم تلقائيًا.',
                'Opens an AI-guided chat that collects data & auto-generates the submission.',
              )}
              testid="task-editor-mode-ai"
            />
            <ModeCard
              active={form.mode === 'form'}
              onClick={() => setF('mode', 'form')}
              icon={ListTodo}
              title={t('نموذج (Form) مخصّص', 'Custom Form')}
              desc={t(
                'يفتح للوكيل نموذجاً ديناميكياً من الحقول التي أنشأتَها.',
                'Opens a dynamic form built from the fields you configure below.',
              )}
              testid="task-editor-mode-form"
            />
          </div>

          {form.mode === 'ai' && (
            <div>
              <div className="text-sm font-bold text-white/80 mb-2">
                {t('اختر وكيل AI', 'Pick an AI agent')}
              </div>
              <AiAgentPicker
                value={form.ai_agent_id}
                onChange={(id) => setF('ai_agent_id', id)}
                isAr={isAr}
              />
            </div>
          )}

          {form.mode === 'form' && (
            <FormBuilder
              schema={form.form_schema}
              onAdd={addField}
              onRemove={removeField}
              onUpdate={updField}
              t={t}
              isAr={isAr}
            />
          )}
        </section>

        {/* Section 4: Status */}
        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-white mb-1">
                {t('حالة التفعيل', 'Activation Status')}
              </h2>
              <p className="text-white/50 text-sm">
                {t(
                  'المهام النشطة فقط تظهر للوكلاء على منصة التجار. الحفظ كمسودة يخفيها عنهم.',
                  'Only active tasks are visible to agents on the merchant platform. Draft hides them.',
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${form.status === 'active' ? 'text-emerald-400' : 'text-white/40'}`}>
                {form.status === 'active' ? t('نشطة', 'Active') : t('مسودة', 'Draft')}
              </span>
              <button
                onClick={() => setF('status', form.status === 'active' ? 'draft' : 'active')}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  form.status === 'active' ? 'bg-emerald-500' : 'bg-white/10'
                }`}
                data-testid="task-editor-status-toggle"
              >
                <span
                  className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${
                    form.status === 'active'
                      ? isAr ? 'right-1' : 'left-7'
                      : isAr ? 'right-7' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>
        </div>
      </main>
    </div>
  );
}

// ─── Small helpers ───────────────────────────────────────────────

function Field({ label, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-white/60 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ModeCard({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-start p-4 rounded-xl border-2 transition-all ${
        active
          ? 'border-cyan-500 bg-cyan-500/10'
          : 'border-white/10 bg-white/[0.02] hover:border-white/20'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${active ? 'bg-cyan-500/30' : 'bg-white/5'}`}>
          <Icon className={`w-5 h-5 ${active ? 'text-cyan-300' : 'text-white/60'}`} />
        </div>
        <div className="flex-1">
          <div className={`font-bold text-sm ${active ? 'text-cyan-200' : 'text-white'}`}>{title}</div>
          <div className="text-xs text-white/50 mt-1 leading-relaxed">{desc}</div>
        </div>
      </div>
    </button>
  );
}

function FormBuilder({ schema, onAdd, onRemove, onUpdate, t, isAr }) {
  return (
    <div>
      <div className="text-sm font-bold text-white/80 mb-3">
        {t('حقول النموذج', 'Form Fields')} ({schema.length})
      </div>

      {/* Field type toolbar */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FIELD_TYPES.map((ft) => {
          const Icon = ft.icon;
          return (
            <button
              key={ft.id}
              onClick={() => onAdd(ft.id)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 text-white/80 text-xs transition"
              data-testid={`task-editor-add-field-${ft.id}`}
            >
              <Icon className="w-4 h-4 text-cyan-400" />
              <Plus className="w-3 h-3" />
              {isAr ? ft.ar : ft.en}
            </button>
          );
        })}
      </div>

      {/* Fields list */}
      {schema.length === 0 ? (
        <div className="text-center py-10 text-white/40 border-2 border-dashed border-white/10 rounded-xl">
          <ListTodo className="w-10 h-10 mx-auto mb-2 opacity-50" />
          {t('اضغط أي زر أعلاه لإضافة حقل جديد.', 'Click any button above to add a field.')}
        </div>
      ) : (
        <div className="space-y-3">
          {schema.map((f, i) => (
            <FieldEditor key={f.id} field={f} index={i} onRemove={onRemove} onUpdate={onUpdate} t={t} isAr={isAr} />
          ))}
        </div>
      )}
    </div>
  );
}

function FieldEditor({ field, index, onRemove, onUpdate, t, isAr }) {
  const typeDef = FIELD_TYPES.find((x) => x.id === field.field_type);
  const Icon = typeDef?.icon || Type;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-[#0a0a0a] rounded-xl border border-white/10"
      data-testid={`task-editor-field-${index}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-white/80 text-sm font-bold">
          <GripVertical className="w-4 h-4 text-white/30" />
          <Icon className="w-4 h-4 text-cyan-400" />
          {isAr ? typeDef?.ar : typeDef?.en}
          <span className="text-white/30 text-xs">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate(index, { required: e.target.checked })}
              className="accent-cyan-500"
              data-testid={`task-editor-field-required-${index}`}
            />
            {t('مطلوب', 'Required')}
          </label>
          <button
            onClick={() => onRemove(index)}
            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
            data-testid={`task-editor-field-remove-${index}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          type="text"
          value={field.label_ar}
          onChange={(e) => onUpdate(index, { label_ar: e.target.value })}
          placeholder={t('السؤال (عربي) *', 'Question (AR) *')}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:border-cyan-500 outline-none"
          data-testid={`task-editor-field-label-ar-${index}`}
        />
        <input
          type="text"
          value={field.label_en}
          onChange={(e) => onUpdate(index, { label_en: e.target.value })}
          placeholder={t('السؤال (إنجليزي)', 'Question (EN)')}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:border-cyan-500 outline-none"
          dir="ltr"
          data-testid={`task-editor-field-label-en-${index}`}
        />
      </div>

      {/* File-type extras */}
      {field.field_type === 'file' && (
        <div className="mt-3 p-3 bg-white/[0.02] border border-white/5 rounded-lg">
          <div className="text-xs font-bold text-white/60 mb-2">
            {t('أنواع الملفات المسموحة', 'Allowed File Types')}
          </div>
          <div className="flex flex-wrap gap-2">
            {FILE_TYPES.map((ext) => {
              const active = (field.allowed_types || []).includes(ext);
              return (
                <button
                  key={ext}
                  onClick={() =>
                    onUpdate(index, {
                      allowed_types: active
                        ? field.allowed_types.filter((x) => x !== ext)
                        : [...(field.allowed_types || []), ext],
                    })
                  }
                  className={`px-2.5 py-1 rounded-md text-xs font-mono font-bold transition ${
                    active
                      ? 'bg-cyan-500 text-black'
                      : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}
                  data-testid={`task-editor-field-filetype-${index}-${ext}`}
                >
                  .{ext}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-white/60">
            <span>{t('الحد الأقصى للحجم (MB):', 'Max size (MB):')}</span>
            <input
              type="number"
              min={1}
              max={100}
              value={field.max_size_mb}
              onChange={(e) => onUpdate(index, { max_size_mb: parseInt(e.target.value, 10) || 10 })}
              className="w-20 px-2 py-1 rounded bg-black/40 border border-white/10 text-white text-xs focus:border-cyan-500 outline-none"
              data-testid={`task-editor-field-maxsize-${index}`}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}
